import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation, useOutletContext } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { createArticle, getArticleById, updateArticle } from '../services/articleService';
import './ArticleEditorPage.css';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const ArticleEditorPage = () => {
  const { articleId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useOutletContext() ?? {};
  const { lang, t } = useLanguage();
  const s = t.articles;

  const isEdit = Boolean(articleId);
  const articlesPath = lang === 'he' ? '/heb/articles' : '/articles';

  const defaultAuthorName =
    currentUser?.fullName ||
    [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ').trim() ||
    currentUser?.login ||
    '';

  const [form, setForm] = useState({
    title: '',
    summary: '',
    content: '',
    authorName: defaultAuthorName,
    tags: '',
    lang: lang,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [isLoading, setIsLoading] = useState(isEdit && !location.state?.article);

  const imageInputRef = useRef(null);

  // Redirect non-admins
  useEffect(() => {
    if (currentUser && currentUser.role !== 'ADMIN') {
      navigate(articlesPath);
    }
  }, [currentUser, navigate, articlesPath]);

  // Load existing article for edit
  useEffect(() => {
    const passedArticle = location.state?.article;
    if (isEdit && passedArticle) {
      setForm({
        title: passedArticle.title ?? '',
        summary: passedArticle.summary ?? '',
        content: passedArticle.content ?? '',
        authorName: passedArticle.authorName ?? defaultAuthorName,
        tags: (passedArticle.tags ?? []).join(', '),
        lang: passedArticle.lang ?? lang,
      });
      if (passedArticle.imageUrl) setImagePreview(passedArticle.imageUrl);
      setIsLoading(false);
      return;
    }

    if (!isEdit) return;

    const controller = new AbortController();
    setIsLoading(true);
    getArticleById(articleId, controller.signal)
      .then((data) => {
        if (!data) return;
        setForm({
          title: data.title ?? '',
          summary: data.summary ?? '',
          content: data.content ?? '',
          authorName: data.authorName ?? defaultAuthorName,
          tags: (data.tags ?? []).join(', '),
          lang: data.lang ?? lang,
        });
        if (data.imageUrl) setImagePreview(data.imageUrl);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setFormError(s.errorLoad);
      })
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, [articleId, isEdit, lang, location.state, s.errorLoad]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      setFormError(s.imageTooLarge);
      e.target.value = '';
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim() || !form.content.trim()) {
      setFormError('Title and content are required.');
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    const tags = form.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

    const articleData = {
      title: form.title.trim(),
      summary: form.summary.trim() || undefined,
      content: form.content.trim(),
      authorName: form.authorName.trim() || undefined,
      tags,
      lang: form.lang,
    };

    try {
      if (isEdit) {
        await updateArticle(articleId, articleData, imageFile || null);
      } else {
        await createArticle(articleData, imageFile || null);
      }
      navigate(articlesPath);
    } catch (err) {
      setFormError(isEdit ? s.errorEdit : s.errorCreate);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isEdit) {
      navigate(
        lang === 'he'
          ? `/heb/articles/${articleId}`
          : `/articles/${articleId}`
      );
    } else {
      navigate(articlesPath);
    }
  };

  if (isLoading) {
    return (
      <div className="article-editor-page">
        <main className="article-editor-main">
          <p className="article-editor-status">{s.loading}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="article-editor-page">
      <main className="article-editor-main">
        <h1 className="article-editor-heading">
          {isEdit ? s.editorTitleEdit : s.editorTitle}
        </h1>

        <form className="article-editor-form" onSubmit={handleSubmit} noValidate>
          {/* Title */}
          <div className="article-editor-field">
            <label className="article-editor-label" htmlFor="ae-title">
              {s.titleLabel}
            </label>
            <input
              id="ae-title"
              name="title"
              type="text"
              className="article-editor-input"
              placeholder={s.titlePlaceholder}
              value={form.title}
              onChange={handleChange}
              required
              maxLength={200}
            />
          </div>

          {/* Summary */}
          <div className="article-editor-field">
            <label className="article-editor-label" htmlFor="ae-summary">
              {s.summaryLabel}
            </label>
            <input
              id="ae-summary"
              name="summary"
              type="text"
              className="article-editor-input"
              placeholder={s.summaryPlaceholder}
              value={form.summary}
              onChange={handleChange}
              maxLength={500}
            />
          </div>

          {/* Author */}
          <div className="article-editor-field">
            <label className="article-editor-label" htmlFor="ae-authorName">
              {s.authorLabel}
            </label>
            <input
              id="ae-authorName"
              name="authorName"
              type="text"
              className="article-editor-input"
              placeholder={s.authorPlaceholder}
              value={form.authorName}
              onChange={handleChange}
              maxLength={80}
            />
          </div>

          {/* Content */}
          <div className="article-editor-field">
            <label className="article-editor-label" htmlFor="ae-content">
              {s.contentLabel}
            </label>
            <textarea
              id="ae-content"
              name="content"
              className="article-editor-textarea"
              placeholder={s.contentPlaceholder}
              value={form.content}
              onChange={handleChange}
              rows={14}
              required
            />
          </div>

          {/* Tags */}
          <div className="article-editor-field">
            <label className="article-editor-label" htmlFor="ae-tags">
              {s.tagsLabel}
            </label>
            <input
              id="ae-tags"
              name="tags"
              type="text"
              className="article-editor-input"
              placeholder={s.tagsPlaceholder}
              value={form.tags}
              onChange={handleChange}
            />
            <span className="article-editor-hint">{s.tagsHelp}</span>
          </div>

          {/* Language */}
          <div className="article-editor-field article-editor-field--row">
            <span className="article-editor-label">Language</span>
            <div className="article-editor-lang-toggle">
              <button
                type="button"
                className={`lang-btn${form.lang === 'en' ? ' lang-btn--active' : ''}`}
                onClick={() => setForm((p) => ({ ...p, lang: 'en' }))}
              >
                EN
              </button>
              <button
                type="button"
                className={`lang-btn${form.lang === 'he' ? ' lang-btn--active' : ''}`}
                onClick={() => setForm((p) => ({ ...p, lang: 'he' }))}
              >
                HE
              </button>
            </div>
          </div>

          {/* Image upload */}
          <div className="article-editor-field">
            <label className="article-editor-label">{s.uploadImage}</label>
            <div
              className="article-editor-image-zone"
              onClick={() => imageInputRef.current?.click()}
              onKeyDown={(e) => e.key === 'Enter' && imageInputRef.current?.click()}
              tabIndex={0}
              role="button"
              aria-label={s.uploadImage}
            >
              {imagePreview ? (
                <img src={imagePreview} alt="Cover preview" className="article-editor-image-preview" />
              ) : (
                <span className="article-editor-image-placeholder">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span>{s.uploadImage}</span>
                </span>
              )}
            </div>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="article-editor-image-input"
              onChange={handleImageChange}
              aria-label={s.uploadImage}
            />
          </div>

          {/* Error */}
          {formError && <p className="article-editor-error">{formError}</p>}

          {/* Actions */}
          <div className="article-editor-actions">
            <button
              type="button"
              className="article-editor-btn article-editor-btn--cancel"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {s.cancelButton}
            </button>
            <button
              type="submit"
              className="article-editor-btn article-editor-btn--submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? isEdit
                  ? s.updatingButton
                  : s.publishingButton
                : isEdit
                ? s.updateButton
                : s.publishButton}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ArticleEditorPage;
